import { Button } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NoteDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { formatDateTime } from '../../lib/format';
import { useCustomerContext } from './CustomerLayout';
import { useCustomerNotes } from './api';
import { Section } from './parts';

const PAGE_SIZE = 10;

/**
 * "View Archive Notes": the voided notes of the customer. The reference's columns are # |
 * Customer Id | Notes | Note Type | Sub Note | Priority | Posted By | Posted DateTime and its
 * empty answer is "No Record Found!!". Sub Note is left out (notes here have no sub note).
 * Assumption: the archive holds the notes marked void; the reference's rule was not seen.
 */
export function ArchivedNotesPage() {
  const { customer } = useCustomerContext();
  const [page, setPage] = useState(1);
  const notes = useCustomerNotes(customer.id, { page, pageSize: PAGE_SIZE, archived: 'true' });

  const columns: Column<NoteDto>[] = [
    {
      key: 'number',
      header: '#',
      width: 60,
      render: (_n, i) => (page - 1) * PAGE_SIZE + i + 1,
    },
    { key: 'customerId', header: 'Customer Id', nowrap: true, render: () => customer.customerId },
    { key: 'text', header: 'Notes', render: (n) => n.text },
    { key: 'noteType', header: 'Note Type', render: (n) => n.noteType },
    { key: 'priority', header: 'Priority', render: (n) => n.priority },
    { key: 'createdBy', header: 'Posted By', render: (n) => n.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Posted DateTime',
      nowrap: true,
      render: (n) => formatDateTime(n.createdAt),
    },
  ];

  return (
    <Section
      title="Search Result"
      actions={
        <Button variant="outlined" component={Link} to="..">
          Customer Profile
        </Button>
      }
    >
      <DataTable
        label="Archived notes"
        columns={columns}
        rows={notes.data?.data}
        rowKey={(n) => n.id}
        loading={notes.isFetching}
        error={notes.error}
        onRetry={() => void notes.refetch()}
        emptyText="No Record Found!!"
        page={page}
        pageSize={PAGE_SIZE}
        total={notes.data?.total ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[PAGE_SIZE]}
      />
    </Section>
  );
}
